import { useState, useEffect, useRef } from "react";
import { T } from "../../config/theme.js";
import { api, trackEvent } from "../../hooks/useApi.js";
import { Btn, Input } from "../ui/index.jsx";

const MEM_ICONS = { qa_summary: "💬", user_intent: "🎯", decision_record: "✅" };

export default function QA({ token, holding, onBack }) {
  const [sessions, setSessions] = useState([]);
  const [sid, setSid] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("분석 중...");
  const [showHistory, setShowHistory] = useState(false);
  const [showMemory, setShowMemory] = useState(false);
  const [memories, setMemories] = useState([]);
  const [memMenuId, setMemMenuId] = useState(null);
  const [undoItem, setUndoItem] = useState(null);
  const [greeting, setGreeting] = useState(null);
  const [greetingVisible, setGreetingVisible] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [readOnlyDate, setReadOnlyDate] = useState("");
  const [continueLoading, setContinueLoading] = useState(false);
  const bottom = useRef(null);
  const undoTimer = useRef(null);

  useEffect(() => {
    api("/qa/sessions", {}, token).then(list => {
      const filtered = (list || []).filter(s => s.symbol === holding.symbol);
      setSessions(filtered);
      if (filtered.length === 0) newSession();
    }).catch(() => newSession());
    loadMemories();
    // Sprint 13-A: 선제 인사 로드 (non-blocking)
    api(`/qa/greeting?symbol=${encodeURIComponent(holding.symbol)}`, {}, token)
      .then(data => {
        if (data?.greeting) {
          setGreeting(data.greeting);
          setTimeout(() => setGreetingVisible(true), 300); // 스르륵 등장
        }
      })
      .catch(() => {}); // 실패해도 무시
  }, []);

  useEffect(() => { bottom.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  // Sprint 12: Breathing 로딩 텍스트 전환
  useEffect(() => {
    let t1, t2;
    if (loading) {
      setLoadingText("분석 중...");
      t1 = setTimeout(() => setLoadingText("최근 60일 데이터 확인 중..."), 1500);
      t2 = setTimeout(() => setLoadingText("리스크 변동 점검 중..."), 3500);
    }
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [loading]);

  const loadMemories = () => {
    api(`/qa/memory/${holding.symbol}`, {}, token).then(data => setMemories(data || [])).catch(() => setMemories([]));
  };

  const newSession = async () => {
    try {
      const s = await api("/qa/sessions", { method: "POST", body: JSON.stringify({ symbol: holding.symbol, title: `${holding.name} 상담` }) }, token);
      setSid(s.id); setMsgs([]); setShowHistory(false); setIsReadOnly(false); setReadOnlyDate("");
      api("/qa/sessions", {}, token).then(list => setSessions((list || []).filter(ss => ss.symbol === holding.symbol))).catch(() => {});
    } catch (e) { console.error(e); }
  };

  const loadSession = async (sessionId) => {
    setSid(sessionId); setShowHistory(false);
    try {
      const messages = await api(`/qa/sessions/${sessionId}/messages`, {}, token);
      const parsed = (messages || []).map(m => {
        if (m.role === "user") return { role: "user", text: typeof m.content === "string" ? m.content : m.content };
        try { return { role: "ai", data: typeof m.content === "string" ? JSON.parse(m.content) : m.content }; }
        catch { return { role: "ai", data: { answer: m.content } }; }
      });
      setMsgs(parsed);

      // Sprint 13 Phase 2: 최신 세션인지 과거 세션인지 판별
      const isLatest = sessions.length > 0 && sessions[0].id === sessionId;
      if (isLatest) {
        setIsReadOnly(false); setReadOnlyDate("");
      } else {
        // 과거 세션 → 읽기 전용
        const session = sessions.find(s => s.id === sessionId);
        const dateStr = session?.created_at ? new Date(session.created_at).toLocaleDateString("ko-KR") : "";
        setIsReadOnly(true); setReadOnlyDate(dateStr);
      }
    } catch (e) { setMsgs([]); }
  };

  // Sprint 13 Phase 2: 이어서 상담 (새 세션 + Handoff Packet)
  const continueSession = async () => {
    if (continueLoading) return;
    setContinueLoading(true);
    try {
      const result = await api(`/qa/sessions/${sid}/continue`, { method: "POST" }, token);
      if (result?.new_session_id) {
        setSid(result.new_session_id);
        setIsReadOnly(false); setReadOnlyDate("");
        // 첫 AI 메시지 표시
        const firstMsg = result.first_message || "지난 상담의 맥락을 이어받았어요.";
        setMsgs([{ role: "ai", data: { answer: firstMsg, _handoff: true } }]);
        // 세션 목록 갱신
        api("/qa/sessions", {}, token).then(list => setSessions((list || []).filter(ss => ss.symbol === holding.symbol))).catch(() => {});
      }
    } catch (e) {
      console.error("[CONTINUE]", e);
    } finally {
      setContinueLoading(false);
    }
  };

  const send = async (overrideText = null) => {
    // 방어: onClick에서 이벤트 객체가 넘어오는 경우 무시
    const override = (typeof overrideText === "string") ? overrideText : null;
    const q = (override || input).trim();
    if (!q || loading) return;
    let currentSid = sid;
    if (!currentSid) {
      try {
        const s = await api("/qa/sessions", { method: "POST", body: JSON.stringify({ symbol: holding.symbol, title: `${holding.name} 상담` }) }, token);
        if (s?.id) { setSid(s.id); currentSid = s.id; } else return;
      } catch { return; }
    }
    setInput(""); setMsgs(p => [...p, { role: "user", text: q }]); setLoading(true);
    const hadMemory = memories.length > 0;
    try {
      trackEvent("qa.ask", { symbol: holding.symbol, meta: { session_id: currentSid } });
      const r = await api(`/qa/sessions/${currentSid}/messages`, { method: "POST", body: JSON.stringify({ question: q, symbol: holding.symbol }) }, token);
      let answerData = r.answer;
      if (typeof answerData === "string") { try { answerData = JSON.parse(answerData); } catch { answerData = { answer: answerData }; } }
      answerData._hadMemory = hadMemory;
      setMsgs(p => [...p, { role: "ai", data: answerData }]);
      setTimeout(() => loadMemories(), 2000);
    } catch (e) {
      setMsgs(p => [...p, { role: "ai", data: { one_liner: "오류", answer: e.message } }]);
    }
    setLoading(false);
  };

  // ── 메모리 관리 ──
  const handlePin = async (memId) => {
    const mem = memories.find(m => m.id === memId);
    if (!mem?.is_pinned && memories.filter(m => m.is_pinned).length >= 3) {
      alert("핀은 최대 3개까지 가능해요. 기존 핀을 해제해주세요.");
      return;
    }
    try {
      await api(`/qa/memory/${memId}/pin`, { method: "PATCH" }, token);
      setMemories(prev => prev.map(m => m.id === memId ? { ...m, is_pinned: !m.is_pinned } : m));
    } catch (e) { console.error(e); }
    setMemMenuId(null);
  };

  const handleDelete = async (memId) => {
    const deleted = memories.find(m => m.id === memId);
    setMemories(prev => prev.filter(m => m.id !== memId));
    setMemMenuId(null);
    setUndoItem(deleted);
    if (undoTimer.current) clearTimeout(undoTimer.current);
    undoTimer.current = setTimeout(async () => {
      try { await api(`/qa/memory/${memId}`, { method: "DELETE" }, token); } catch (e) { console.error(e); }
      setUndoItem(null);
    }, 5000);
  };

  const handleUndo = () => {
    if (undoItem) {
      setMemories(prev => [undoItem, ...prev]);
      if (undoTimer.current) clearTimeout(undoTimer.current);
      setUndoItem(null);
    }
  };

  // Sprint 13-C: 추천 질문 칩 (규칙 기반 MVP)
  const CHIP_DICT = {
    ACTION:      ["일부 정리하는 건 어떻게 생각해?", "정리 기준을 조건으로 정리해줘"],
    EMOTION:     ["지금 정리하면 아쉬울 가능성은?", "비슷한 상황 사례 있어?"],
    CONCEPT:     ["더 쉽게 설명해줘", "이 상황에서 같이 볼 지표는?"],
    COMPARE:     ["둘 다 보유할 때 리스크는?", "리스크 차이만 비교해줘"],
    FOLLOW_UP:   ["핵심 근거 1개만 더 알려줘", "다른 관점은 없어?"],
    FULL_STATUS: ["지금 추가매수 괜찮아?", "가장 주의할 점은?"]
  };
  const classifyForChips = (q) => {
    if (!q) return "FULL_STATUS";
    // ACTION이 EMOTION보다 우선 (오판 방지)
    if (/팔아|사야|추가매수|손절|정리|청산|비중|분할|매도|매수해|팔까/.test(q)) return "ACTION";
    if (/불안|걱정|무서|겁나|힘들|멘탈|스트레스/.test(q)) return "EMOTION";
    if (/뭐야|뜻이|무슨말|의미|뭔지/.test(q)) return "CONCEPT";
    if (/비교|중 뭐|뭐가 나|어디가 나/.test(q)) return "COMPARE";
    if (q.length < 20 || /그게|그럼|왜|아까|방금/.test(q)) return "FOLLOW_UP";
    return "FULL_STATUS";
  };
  // 반복 방지: 최근 2턴과 같은 type이면 rotation
  const recentTypes = msgs.filter(m => m.role === "user").slice(-2).map(m => classifyForChips(m.text));
  const CHIP_ROTATION = { ACTION: "FULL_STATUS", EMOTION: "FOLLOW_UP", CONCEPT: "FOLLOW_UP", COMPARE: "FULL_STATUS", FOLLOW_UP: "CONCEPT", FULL_STATUS: "ACTION" };
  const getChips = (lastUserMsg) => {
    let mode = classifyForChips(lastUserMsg);
    // 같은 type 2번 연속이면 rotation
    if (recentTypes.length >= 2 && recentTypes.every(t => t === mode)) {
      mode = CHIP_ROTATION[mode] || "FULL_STATUS";
    }
    const candidates = CHIP_DICT[mode] || CHIP_DICT.FULL_STATUS;
    const recentUserMsgs = msgs.filter(m => m.role === "user").slice(-3).map(m => m.text);
    return candidates.filter(c => !recentUserMsgs.some(r => r.includes(c.slice(0, 6))));
  };
  const lastUserMsg = [...msgs].reverse().find(m => m.role === "user")?.text;
  const suggestedChips = !loading && msgs.length > 0 && msgs[msgs.length - 1]?.role === "ai" ? getChips(lastUserMsg) : [];

  const pinnedMems = memories.filter(m => m.is_pinned);
  const recentMems = memories.filter(m => !m.is_pinned).slice(0, 7);

  const toggleMemory = () => { setShowMemory(!showMemory); if (!showMemory) setShowHistory(false); };
  const toggleHistory = () => { setShowHistory(!showHistory); if (!showHistory) setShowMemory(false); };

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100dvh - 100px)", minHeight: 0 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Btn variant="ghost" style={{ padding: "6px 12px" }} onClick={onBack}>←</Btn>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>💬 {holding.name}</div>
            <div style={{ fontSize: 11, color: T.textDim }}>{holding.symbol} · 데이터 요약</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <Btn variant="ghost" style={{ fontSize: 11, padding: "5px 10px", background: showMemory ? T.accent + "15" : "transparent" }} onClick={toggleMemory}>
            📋 메모리 ({memories.length})
          </Btn>
          <Btn variant="ghost" style={{ fontSize: 11, padding: "5px 10px", background: showHistory ? T.accent + "15" : "transparent" }} onClick={toggleHistory}>
            📁 기록 ({sessions.length})
          </Btn>
          <Btn variant="small" onClick={newSession}>+ 새 대화</Btn>
        </div>
      </div>

      {/* Memory Panel */}
      {showMemory && (
        <div style={{ marginBottom: 14, maxHeight: 240, overflowY: "auto", borderRadius: 10, border: `1px solid ${T.accent}30`, background: T.bg2 }}>
          {memories.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <div style={{ fontSize: 13, color: T.textDim, marginBottom: 4 }}>아직 메모리가 없어요</div>
              <div style={{ fontSize: 11, color: T.textMuted }}>이 종목에 대해 대화하면 핵심만 자동 저장돼요.</div>
            </div>
          ) : (
            <>
              {pinnedMems.length > 0 && (
                <>
                  <div style={{ padding: "8px 14px 4px", fontSize: 10, color: T.accent, fontWeight: 700 }}>📌 고정</div>
                  {pinnedMems.map(m => <MemoryRow key={m.id} mem={m} menuOpen={memMenuId === m.id} onMenuToggle={() => setMemMenuId(memMenuId === m.id ? null : m.id)} onPin={() => handlePin(m.id)} onDelete={() => handleDelete(m.id)} />)}
                  <div style={{ borderBottom: `1px solid ${T.border}`, margin: "4px 14px" }} />
                </>
              )}
              {recentMems.length > 0 && (
                <>
                  <div style={{ padding: "8px 14px 4px", fontSize: 10, color: T.textMuted, fontWeight: 700 }}>최근</div>
                  {recentMems.map(m => <MemoryRow key={m.id} mem={m} menuOpen={memMenuId === m.id} onMenuToggle={() => setMemMenuId(memMenuId === m.id ? null : m.id)} onPin={() => handlePin(m.id)} onDelete={() => handleDelete(m.id)} />)}
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Session History Panel */}
      {showHistory && (
        <div style={{ marginBottom: 14, maxHeight: 200, overflowY: "auto", borderRadius: 10, border: `1px solid ${T.border}`, background: T.bg2 }}>
          {sessions.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", fontSize: 12, color: T.textMuted }}>아직 대화 기록이 없어요</div>
          ) : sessions.map(s => (
            <div key={s.id} onClick={() => loadSession(s.id)} style={{ padding: "10px 14px", cursor: "pointer", borderBottom: `1px solid ${T.border}`, background: s.id === sid ? T.accent + "15" : "transparent", transition: "background 0.15s", display: "flex", justifyContent: "space-between", alignItems: "center" }}
              onMouseEnter={e => { if (s.id !== sid) e.currentTarget.style.background = T.cardHover; }}
              onMouseLeave={e => { if (s.id !== sid) e.currentTarget.style.background = "transparent"; }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: s.id === sid ? T.accent : T.text }}>{s.title || `${holding.name} 상담`}</div>
                <div style={{ fontSize: 11, color: T.textMuted }}>{new Date(s.created_at).toLocaleDateString("ko-KR")} {new Date(s.created_at).toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              {s.id === sid && <span style={{ fontSize: 10, color: T.accent, fontWeight: 700 }}>현재</span>}
            </div>
          ))}
        </div>
      )}

      {/* Sprint 13 Phase 2: 읽기 전용 상단 배너 */}
      {isReadOnly && (
        <div style={{ padding: "10px 16px", background: T.yellow + "12", border: `1px solid ${T.yellow}30`, borderRadius: 10, marginBottom: 10, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 13 }}>🔒</span>
          <span style={{ fontSize: 12, color: T.yellow, fontWeight: 600 }}>기록 (읽기 전용) — {readOnlyDate || "과거"} 데이터 기준</span>
        </div>
      )}

      {/* Chat Area */}
      <div style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column", gap: 14, paddingRight: 4 }}>
        {msgs.length === 0 && (
          <div style={{ textAlign: "center", padding: greeting ? 30 : 60, color: T.textDim }}>
            {/* Sprint 13-A: Contextual Greeting */}
            {greeting && greetingVisible && (
              <div style={{ marginBottom: 20, padding: "14px 18px", borderRadius: 14, background: T.card, border: `1px solid ${T.accent}30`, textAlign: "left", animation: "greetingSlideIn 0.4s ease-out", position: "relative" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 11, color: T.accent, fontWeight: 700 }}>⏸ Pause 비서</span>
                  <span onClick={() => setGreetingVisible(false)} style={{ fontSize: 14, color: T.textMuted, cursor: "pointer", padding: "0 2px", lineHeight: 1 }}
                    onMouseEnter={e => e.currentTarget.style.color = T.text}
                    onMouseLeave={e => e.currentTarget.style.color = T.textMuted}>×</span>
                </div>
                <div style={{ fontSize: 14, color: T.text, lineHeight: 1.7 }}>{greeting}</div>
              </div>
            )}
            <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>💬</div>
            <div style={{ fontSize: 14, marginBottom: 8 }}>{holding.name}의 데이터를 확인해보세요</div>
            <div style={{ fontSize: 12, lineHeight: 1.8 }}>"최근 거래량 변화 알려줘"<br />"이번 분기 실적 정리해줘"<br />"최근 공시 요약해줘"</div>
          </div>
        )}
        {msgs.map((m, i) => {
          let d = m.data;
          if (m.role === "ai" && d) {
            if (typeof d === "string") { try { d = JSON.parse(d); } catch { d = { answer: d }; } }
            if (typeof d.answer === "string" && d.answer.startsWith("{")) { try { const inner = JSON.parse(d.answer); d = { ...d, ...inner }; } catch {} }
            if (d.answer) d.answer = d.answer.replace(/\\n/g, "\n");
          }
          return (
            <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
              <div style={{ maxWidth: "80%", padding: "14px 18px", borderRadius: 14, background: m.role === "user" ? T.accent : T.card, border: m.role === "user" ? "none" : `1px solid ${T.border}`, color: T.text, fontSize: 14, lineHeight: 1.7 }}>
                {m.role === "user" ? m.text : (
                  <div>
                    {d?.one_liner && <div style={{ fontWeight: 700, color: T.accent, marginBottom: 8, fontSize: 15 }}>{d.one_liner}</div>}
                    <div style={{ whiteSpace: "pre-wrap" }}>{d?.answer}</div>
                    {d?.conditions?.length > 0 && (
                      <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
                        {d.conditions.map((c, j) => <div key={j} style={{ fontSize: 12, color: T.yellow, marginTop: 4 }}>⟐ {c}</div>)}
                      </div>
                    )}
                    {d?.checkpoint && <div style={{ fontSize: 11, color: T.textMuted, marginTop: 10 }}>◎ 다음 체크: {d.checkpoint}</div>}
                    {d?._hadMemory && (
                      <div onClick={(e) => { e.stopPropagation(); toggleMemory(); }} style={{ marginTop: 10, display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 8px", borderRadius: 6, background: T.accent + "10", cursor: "pointer", transition: "background 0.15s" }}
                        onMouseEnter={e => e.currentTarget.style.background = T.accent + "20"}
                        onMouseLeave={e => e.currentTarget.style.background = T.accent + "10"}>
                        <span style={{ fontSize: 10 }}>📋</span>
                        <span style={{ fontSize: 10, color: T.accent, fontWeight: 600 }}>메모리 반영</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <div className="breathing-bg" style={{ padding: "14px 20px", borderRadius: 14, color: T.accent, fontWeight: 700, fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}>
              <span className="pulse-anim">✨</span> {loadingText}
            </div>
          </div>
        )}
        <div ref={bottom} />
      </div>

      {/* Sprint 13-C: 추천 질문 칩 */}
      {suggestedChips.length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10, animation: "chipFadeIn 0.25s ease-out" }}>
          {suggestedChips.map((chip, i) => (
            <div key={i} onClick={() => send(chip)} style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${T.accent}40`, background: T.accent + "10", fontSize: 12, color: T.accent, fontWeight: 600, cursor: "pointer", transition: "all 0.15s", whiteSpace: "nowrap" }}
              onMouseEnter={e => { e.currentTarget.style.background = T.accent + "25"; e.currentTarget.style.borderColor = T.accent; }}
              onMouseLeave={e => { e.currentTarget.style.background = T.accent + "10"; e.currentTarget.style.borderColor = T.accent + "40"; }}>
              {chip}
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      {/* Sprint 13 Phase 2: 읽기 전용이면 CTA 배너, 아니면 입력창 */}
      {isReadOnly ? (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
          <div style={{ textAlign: "center", padding: "16px 20px", background: T.card, borderRadius: 14, border: `1px solid ${T.accent}30` }}>
            <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 10 }}>
              🔒 이 대화는 {readOnlyDate || "과거"} 데이터 기준 기록입니다
            </div>
            <div onClick={continueSession} style={{
              padding: "12px 24px", background: T.accent, color: "#fff", borderRadius: 10,
              fontSize: 14, fontWeight: 700, cursor: continueLoading ? "wait" : "pointer",
              opacity: continueLoading ? 0.6 : 1, transition: "opacity 0.2s", display: "inline-block"
            }}>
              {continueLoading ? "준비 중..." : "✨ 최신 데이터로 이어서 상담하기"}
            </div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 8 }}>
              새 세션으로 시작되며, 과거 수치/상태는 가져오지 않습니다
            </div>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
          <input
            type="text"
            inputMode="text"
            enterKeyHint="send"
            style={{ flex: 1, background: T.card, color: T.text, border: `1px solid ${T.border}`, padding: "12px 16px", borderRadius: 10, fontSize: 14, outline: "none", fontFamily: "inherit", boxSizing: "border-box", WebkitAppearance: "none" }}
            placeholder="질문을 입력하세요..."
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.nativeEvent.isComposing) { e.preventDefault(); send(); } }}
            onFocus={e => { e.target.style.borderColor = T.accent + "60"; setTimeout(() => e.target.scrollIntoView({ behavior: "smooth", block: "center" }), 300); }}
            onBlur={e => { e.target.style.borderColor = T.border; }}
          />
          <button
            type="button"
            onMouseDown={e => e.preventDefault()}
            onClick={() => send()}
            disabled={loading}
            style={{ background: T.accent, color: "#fff", border: "none", borderRadius: 10, padding: "10px 22px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: loading ? 0.5 : 1, flexShrink: 0, fontFamily: "inherit", WebkitAppearance: "none", touchAction: "manipulation" }}
          >전송</button>
        </div>
      )}

      {/* Undo 토스트 */}
      {undoItem && (
        <div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 16px", display: "flex", alignItems: "center", gap: 12, zIndex: 100, boxShadow: "0 4px 20px rgba(0,0,0,0.4)" }}>
          <span style={{ fontSize: 13, color: T.text }}>메모리 삭제됨</span>
          <span onClick={handleUndo} style={{ fontSize: 12, color: T.accent, fontWeight: 700, cursor: "pointer" }}>실행 취소</span>
        </div>
      )}
    </div>
  );
}

function MemoryRow({ mem, menuOpen, onMenuToggle, onPin, onDelete }) {
  const date = new Date(mem.created_at);
  const dateStr = `${date.getMonth() + 1}/${date.getDate()}`;
  return (
    <div style={{ padding: "8px 14px", display: "flex", alignItems: "center", gap: 8, background: mem.is_pinned ? T.accent + "08" : "transparent", position: "relative" }}>
      <span style={{ fontSize: 13, flexShrink: 0 }}>{MEM_ICONS[mem.memory_type] || "💬"}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12, color: T.text, overflow: "hidden", textOverflow: "ellipsis", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", lineHeight: 1.5, fontWeight: mem.is_pinned ? 600 : 400 }}>{mem.content}</div>
      </div>
      <span style={{ fontSize: 10, color: T.textMuted, flexShrink: 0 }} title={date.toLocaleString("ko-KR")}>{dateStr}</span>
      <span onClick={(e) => { e.stopPropagation(); onPin(); }} style={{ fontSize: 12, cursor: "pointer", opacity: mem.is_pinned ? 1 : 0.3, transition: "opacity 0.15s", flexShrink: 0 }}
        onMouseEnter={e => { if (!mem.is_pinned) e.currentTarget.style.opacity = "0.7"; }}
        onMouseLeave={e => { if (!mem.is_pinned) e.currentTarget.style.opacity = "0.3"; }}>📌</span>
      <span onClick={(e) => { e.stopPropagation(); onMenuToggle(); }} style={{ fontSize: 14, cursor: "pointer", color: T.textMuted, flexShrink: 0, padding: "0 2px" }}>⋯</span>
      {menuOpen && (
        <div style={{ position: "absolute", right: 14, top: "100%", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 0", zIndex: 50, boxShadow: "0 4px 12px rgba(0,0,0,0.3)", minWidth: 80 }}>
          <div onClick={(e) => { e.stopPropagation(); onDelete(); }} style={{ padding: "6px 14px", fontSize: 12, color: T.red, cursor: "pointer", transition: "background 0.1s" }}
            onMouseEnter={e => e.currentTarget.style.background = T.red + "15"}
            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>삭제</div>
        </div>
      )}
    </div>
  );
}
